'use client';

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import type { Booking } from '@/types';
import { formatMoney } from '@/lib/currency';
import { bookingOverlaps } from '@/lib/morocco';
import {
  chunkWeeks,
  monthCells,
  overflowOnColumn,
  weekBars,
  type CalendarBar,
} from '@/lib/desk/reservation-calendar';
import { bookingTone, statusToneClass } from './desk-feedback';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const VISIBLE_LANES = 3;

type DayRole = 'pickup' | 'return' | 'on-rent' | 'same-day';

const ROLE_LABEL: Record<DayRole, string> = {
  pickup: 'Pickup',
  return: 'Return',
  'on-rent': 'On rent',
  'same-day': 'Same day',
};

interface ReservationCalendarProps {
  year: number;
  month: number;
  bookings: Booking[];
  fleetCount: number;
  currency: string;
  selectedDay: string | null;
  onSelectDay: (day: string | null) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}

function localToday() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function dayRole(booking: Booking, day: string): DayRole {
  const starts = booking.pickupDate === day;
  const ends = booking.dropoffDate === day;
  if (starts && ends) return 'same-day';
  if (starts) return 'pickup';
  if (ends) return 'return';
  return 'on-rent';
}

function shortLabel(booking: Booking) {
  const vehicle = booking.vehicleName.length > 14 ? `${booking.vehicleName.slice(0, 13)}…` : booking.vehicleName;
  const customer = booking.customerName.length > 12 ? `${booking.customerName.slice(0, 11)}…` : booking.customerName;
  return `${vehicle} · ${customer}`;
}

export function ReservationCalendar({
  year,
  month,
  bookings,
  fleetCount,
  currency,
  selectedDay,
  onSelectDay,
  onPrev,
  onNext,
  onToday,
}: ReservationCalendarProps) {
  const [today, setToday] = useState('');
  useEffect(() => {
    setToday(localToday());
  }, []);
  const weeks = useMemo(() => chunkWeeks(monthCells(year, month)), [year, month]);
  const monthLabel = new Date(year, month, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

  const pickups = bookings.filter((booking) => booking.pickupDate.startsWith(monthKey)).length;
  const returns = bookings.filter((booking) => booking.dropoffDate.startsWith(monthKey)).length;
  const busiest = weeks.flat().reduce((peak, day) => {
    if (!day) return peak;
    const count = bookings.filter((booking) => bookingOverlaps(day, day, booking.pickupDate, booking.dropoffDate)).length;
    return count > peak ? count : peak;
  }, 0);

  const selectedBookings = selectedDay
    ? bookings
      .filter((booking) => bookingOverlaps(selectedDay, selectedDay, booking.pickupDate, booking.dropoffDate))
      .slice()
      .sort((left, right) => left.pickupTime.localeCompare(right.pickupTime))
    : [];

  const selectedLabel = selectedDay
    ? new Date(`${selectedDay}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
    : '';

  return (
    <section className="w-full max-w-full overflow-hidden rounded-3xl border border-line bg-card shadow-sm">
      <header className="border-b border-line bg-shell/60 px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="type-table-header text-brand">Customer reservations</p>
            <h3 className="type-section mt-1 text-ink">{monthLabel}</h3>
            <p className="mt-1 text-xs text-muted">{bookings.length} in view · peak {busiest} on one day</p>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={onPrev} aria-label="Previous month" className="rounded-xl border border-line bg-card p-2 text-ink hover:border-brand">
              <ChevronLeft size={16} />
            </button>
            <button type="button" onClick={onToday} className="rounded-xl border border-line bg-card px-3 py-2 text-xs font-semibold text-ink hover:border-brand">
              Today
            </button>
            <button type="button" onClick={onNext} aria-label="Next month" className="rounded-xl border border-line bg-card p-2 text-ink hover:border-brand">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[
            { label: 'Pickups', value: pickups },
            { label: 'Returns', value: returns },
            { label: 'Fleet', value: fleetCount },
          ].map((stat) => (
            <div key={stat.label} className="rounded-2xl border border-line bg-card px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">{stat.label}</p>
              <p className="mt-0.5 text-lg font-semibold tabular-nums text-ink">{stat.value}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold">
          {(['pending', 'confirmed', 'active', 'completed'] as const).map((status) => (
            <span key={status} className={`rounded-full border px-2 py-0.5 capitalize ${statusToneClass(bookingTone(status))}`}>{status}</span>
          ))}
        </div>
      </header>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0">
            <div className="grid w-full border-b border-line bg-shell/40 [grid-template-columns:repeat(7,minmax(0,1fr))]">
              {WEEKDAYS.map((day, index) => (
                <div key={day} className={`px-2 py-2 text-[11px] font-semibold uppercase tracking-wide ${index >= 5 ? 'text-brand' : 'text-muted'}`}>
                  {day}
                </div>
              ))}
            </div>
            {weeks.map((week) => (
              <WeekRow
                key={week.find((day) => day) ?? 'empty'}
                days={week}
                bookings={bookings}
                today={today}
                selectedDay={selectedDay}
                onSelectDay={onSelectDay}
              />
            ))}
        </div>

        <aside className="border-t border-line bg-shell/40 lg:border-s lg:border-t-0">
          <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
            <div>
              <p className="type-table-header text-muted">Day desk</p>
              <p className="mt-1 text-sm font-semibold text-ink">{selectedDay ? selectedLabel : 'Choose a day'}</p>
            </div>
            {selectedDay && (
              <button type="button" onClick={() => onSelectDay(null)} className="text-xs font-semibold text-brand">
                Clear
              </button>
            )}
          </div>
          <div className="max-h-[640px] space-y-2 overflow-y-auto p-3">
            {!selectedDay && (
              <p className="rounded-2xl border border-dashed border-line px-3 py-6 text-center text-xs text-muted">
                Select a day to see pickups, returns, and cars still out. The list below follows the same day.
              </p>
            )}
            {selectedDay && selectedBookings.length === 0 && (
              <p className="rounded-2xl border border-dashed border-line px-3 py-6 text-center text-xs text-muted">
                Nothing is on the fleet this day.
              </p>
            )}
            {selectedBookings.map((booking) => {
              const role = dayRole(booking, selectedDay ?? '');
              const balance = booking.totalPrice - (booking.avanceAmount ?? 0);
              return (
                <article key={booking.id} className="rounded-2xl border border-line bg-card p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-ink">{booking.customerName}</p>
                      <p className="truncate text-xs text-muted">{booking.vehicleName}</p>
                    </div>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${statusToneClass(bookingTone(booking.status))}`}>
                      {booking.status}
                    </span>
                  </div>
                  <p className="mt-2 inline-flex rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
                    {ROLE_LABEL[role]}
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                    <div className="rounded-xl bg-shell px-2 py-1.5">
                      <p className="text-muted">Out</p>
                      <p className="font-semibold text-ink">{booking.pickupTime}</p>
                      <p className="truncate text-muted">{booking.pickupLocation}</p>
                    </div>
                    <div className="rounded-xl bg-shell px-2 py-1.5">
                      <p className="text-muted">Back</p>
                      <p className="font-semibold text-ink">{booking.dropoffTime}</p>
                      <p className="truncate text-muted">{booking.dropoffLocation}</p>
                    </div>
                  </div>
                  <p className="mt-2 flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1 text-muted"><MapPin size={12} /> {booking.rentalDays}d</span>
                    <span className={balance > 0 ? 'font-semibold text-warning' : 'font-semibold text-success'}>
                      Due {formatMoney(balance, currency)}
                    </span>
                  </p>
                </article>
              );
            })}
          </div>
        </aside>
      </div>
    </section>
  );
}

function WeekRow({
  days,
  bookings,
  today,
  selectedDay,
  onSelectDay,
}: {
  days: (string | null)[];
  bookings: Booking[];
  today: string;
  selectedDay: string | null;
  onSelectDay: (day: string | null) => void;
}) {
  const bars = weekBars(days, bookings);
  const bookingById = new Map(bookings.map((booking) => [booking.id, booking]));
  const usedLanes = bars.reduce((highest, bar) => Math.max(highest, bar.lane + 1), 1);
  const shownLanes = Math.min(VISIBLE_LANES, usedLanes);

  return (
    <div
      className="grid w-full border-b border-line [grid-template-columns:repeat(7,minmax(0,1fr))]"
      style={{ gridTemplateRows: `2rem repeat(${shownLanes}, 1.35rem)` }}
    >
      {days.map((day, column) => {
        const selected = day !== null && day === selectedDay;
        const overflow = day ? overflowOnColumn(column, bars, VISIBLE_LANES) : 0;
        return (
          <button
            key={day ?? `empty-${column}`}
            type="button"
            disabled={!day}
            onClick={() => day && onSelectDay(selected ? null : day)}
            style={{ gridColumn: column + 1, gridRow: `1 / span ${shownLanes + 1}` }}
            className={`relative z-0 min-w-0 border-e border-line px-1.5 py-1 text-left last:border-e-0 ${
              !day ? 'bg-shell/50' : selected ? 'bg-brand/10' : column >= 5 ? 'bg-shell/30 hover:bg-brand/5' : 'bg-card hover:bg-brand/5'
            }`}
          >
            {day && (
              <span className="flex items-center justify-between">
                <span className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums ${
                  day === today ? 'bg-brand text-slate-950' : selected ? 'text-brand' : 'text-ink'
                }`}
                >
                  {Number(day.slice(-2))}
                </span>
                {overflow > 0 && <span className="text-[10px] font-semibold text-muted">+{overflow}</span>}
              </span>
            )}
          </button>
        );
      })}
      {bars.filter((bar) => bar.lane < VISIBLE_LANES).map((bar) => (
        <ReservationBar
          key={`${bar.id}-${bar.startCol}`}
          bar={bar}
          booking={bookingById.get(bar.id)}
          onSelectDay={onSelectDay}
          days={days}
        />
      ))}
    </div>
  );
}

function ReservationBar({
  bar,
  booking,
  days,
  onSelectDay,
}: {
  bar: CalendarBar;
  booking: Booking | undefined;
  days: (string | null)[];
  onSelectDay: (day: string | null) => void;
}) {
  if (!booking) return null;
  const openDay = days[bar.startCol];
  return (
    <button
      type="button"
      title={`${booking.vehicleName} · ${booking.customerName} · ${booking.pickupDate} → ${booking.dropoffDate}`}
      onClick={() => openDay && onSelectDay(openDay)}
      style={{ gridColumn: `${bar.startCol + 1} / ${bar.endCol + 2}`, gridRow: bar.lane + 2 }}
      className={`z-10 mx-0.5 min-w-0 truncate border px-1.5 text-left text-[10px] font-semibold ${statusToneClass(bookingTone(booking.status))} ${
        bar.continuesBefore ? 'rounded-l-none border-l-0' : 'rounded-l-md'
      } ${bar.continuesAfter ? 'rounded-r-none border-r-0' : 'rounded-r-md'}`}
    >
      {bar.continuesBefore ? '‹ ' : ''}{shortLabel(booking)}
    </button>
  );
}
